import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const Modal = ({
  show,
  isOpen,
  onHide,
  onClose,
  title,
  children,
  size = "md",
  footer = null,
  className = "",
}) => {
  const isShowing = show || isOpen;
  const hideHandler = onHide || onClose;

  useEffect(() => {
    if (isShowing) {
      document.body.style.overflow = "hidden";
      // Scroll to top of the modal if it's too long
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isShowing]);

  if (!isShowing) return null;

  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    // กว้างกว่า xl สำหรับหน้าต่าง 2 ฝั่ง (รายละเอียดอุปกรณ์ในรอบผลิต) — พอดีเนื้อหา ไม่เหลือขอบขาวกว้างแบบ full
    "2xl": "max-w-6xl",
    // สำหรับตารางกว้าง ๆ เช่นหน้าจับคอลัมน์ไฟล์ BOM — เกือบเต็มจอแต่ยังเหลือขอบ
    full: "max-w-[96rem]",
  };

  return createPortal(
    <div
      // จอเล็ก (มือถือ) = แผ่นเลื่อนขึ้นจากขอบล่าง เต็มความกว้าง · sm ขึ้นไป = กล่องกลางจอเหมือนเดิม (28 ก.ย. 69)
      className={`fixed inset-0 z-[99999] flex items-end sm:items-center justify-center p-0 sm:p-4 ${className}`}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100vw",
        height: "100dvh",
        pointerEvents: "none",
      }}
    >
      {/* Backdrop - Separate layer for guaranteed full coverage */}
      <div
        className="fixed inset-0 bg-black/60 transition-opacity pointer-events-auto"
        style={{ position: "fixed", inset: 0 }}
        onClick={hideHandler}
      ></div>

      {/* Modal Wrapper - The Centering Engine */}
      <div
        className={`relative w-full ${sizeClasses[size]} z-[100000] pointer-events-auto animate-slideDownPop max-h-[92dvh] sm:max-h-[90vh]`}
        style={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="relative flex flex-col w-full bg-white dark:bg-zinc-900 border border-slate-300 rounded-t-md rounded-b-none sm:rounded-md outline-none focus:outline-none overflow-hidden transition-colors duration-500">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 md:px-5 border-b border-solid border-slate-300 bg-slate-50 dark:border-zinc-800 dark:bg-zinc-900">
            <h3 className="text-[17px] font-black text-slate-900 dark:text-white">
              {title}
            </h3>
            <button
              className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 dark:border-zinc-700 dark:bg-zinc-800 dark:text-slate-300"
              aria-label="ปิด"
              onClick={hideHandler}
            >
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div
            className="relative p-4 md:p-5 flex-auto overflow-y-auto text-slate-800 dark:text-slate-200 max-h-[calc(92dvh-140px)] sm:max-h-[calc(90vh-150px)]"
          >
            {children}
          </div>

          {/* Footer (Optional) */}
          {footer && (
            <div className="flex flex-wrap items-center justify-end px-4 py-3 md:px-5 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-solid border-slate-300 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900/50 gap-3">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
